import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Building2,
  CheckCircle,
  ClipboardCheck,
  Clock,
  Crown,
  MailWarning,
  PauseCircle,
  Plus,
  RefreshCw,
  Send,
  ShieldAlert,
  ShieldCheck,
  Users,
  X,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import API_BASE from '../../utils/apiBase';
import PlatformProspectInbox from '../../components/owner/PlatformProspectInbox';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';

const API = API_BASE;

const emptyWorkspaceForm = {
  display_name: '',
  legal_name: '',
  contact_email: '',
  contact_phone: '',
  plan_id: 'essential',
  notes: '',
};

const emptyInviteForm = {
  contact_name: '',
  contact_email: '',
  role: 'workspace_owner',
  notes: '',
};

const emptyBetaTermsForm = {
  accepted_by_name: '',
  accepted_by_email: '',
  notes: '',
  authority_confirmed: false,
  beta_terms_accepted: false,
  privacy_notice_accepted: false,
  data_processing_terms_accepted: false,
  retention_backup_terms_accepted: false,
};

const BETA_ACCEPTANCE_CHECKS = [
  {
    key: 'authority_confirmed',
    label: 'The named person has authority to accept private beta terms for this tenant.',
  },
  {
    key: 'beta_terms_accepted',
    label: 'The tenant accepts invite-only private beta access and understands live use depends on launch checks.',
  },
  {
    key: 'privacy_notice_accepted',
    label: 'The tenant accepts the privacy notice for applicant, worker, referee, compliance and support data.',
  },
  {
    key: 'data_processing_terms_accepted',
    label: 'The tenant accepts CareOps processing data for platform delivery, security, audit, support and lawful obligations.',
  },
  {
    key: 'retention_backup_terms_accepted',
    label: 'The tenant accepts retention, archiving, deletion/anonymisation limits and encrypted backup handling.',
  },
];

const STATUS_OPTIONS = [
  'invite_draft',
  'setup',
  'training',
  'testing',
  'live_beta',
  'active',
  'suspended',
  'offboarding',
  'closed',
  'archived',
];

const CAPABILITY_LABELS = {
  tenant_access: 'Tenant access',
  subscription_billing: 'Subscription billing',
  official_documents: 'Documents and PDFs',
  feature_access: 'Feature access',
  billing: 'Billing',
  email_branding: 'Email branding',
  worker_onboarding: 'Worker onboarding',
  worker_documents: 'Worker documents',
  payroll: 'Payroll',
  invoicing: 'Invoicing',
  finance: 'Finance',
  private_links: 'Private links',
};

function authHeaders(token) {
  return { Authorization: `Bearer ${token}` };
}

function planLabel(plans, planId) {
  return plans.find((plan) => plan.id === planId)?.name || planId || 'No plan';
}

function formatStatus(value) {
  return String(value || 'not set').replace(/_/g, ' ');
}

function statusTone(status) {
  if (status === 'active' || status === 'live_beta') return 'bg-emerald-50 text-emerald-800 border-emerald-200';
  if (status === 'suspended' || status === 'offboarding') return 'bg-amber-50 text-amber-800 border-amber-200';
  if (status === 'closed' || status === 'archived') return 'bg-slate-100 text-slate-700 border-slate-200';
  return 'bg-sky-50 text-sky-800 border-sky-200';
}

function readinessTone(readiness) {
  if (readiness?.can_activate) return 'text-emerald-700';
  return 'text-amber-700';
}

function metric(value) {
  return Number(value || 0).toLocaleString();
}

function openTenantRoute(route) {
  if (!route) return;
  window.open(route, '_blank', 'noopener,noreferrer');
}

function formatDateTime(value) {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function SummaryTile({ icon: Icon, label, value, tone = 'text-slate-900' }) {
  return (
    <div className="rounded-lg border border-[#E4E8EB] bg-white p-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
        <Icon className="h-4 w-4 text-slate-500" />
      </div>
      <p className={`mt-3 text-2xl font-semibold ${tone}`}>{metric(value)}</p>
    </div>
  );
}

export function TenantIsolationPanel({ readiness }) {
  const checks = readiness?.checks || [];
  const blockers = readiness?.blockers || [];
  const passed = checks.filter((check) => check.passed).length;
  const ready = Boolean(readiness?.ready);
  const blockerCategories = Array.from(new Set(blockers.map((blocker) => blocker.category).filter(Boolean)));
  const nextAction = ready
    ? readiness?.environment_flag_enabled
      ? 'Isolation report is green. Continue using per-workspace launch readiness before activation.'
      : 'Isolation report is green. Deployment/operator approval is still needed before enabling external tenant activation.'
    : 'Resolve the blocker categories in the read-only isolation report before enabling tenant invitations or activation.';

  return (
    <section className={`rounded-lg border p-5 ${
      ready ? 'border-emerald-200 bg-emerald-50/70' : 'border-red-200 bg-red-50/70'
    }`} data-testid="tenant-isolation-panel">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            {ready ? (
              <ShieldCheck className="h-4 w-4 text-emerald-700" />
            ) : (
              <ShieldAlert className="h-4 w-4 text-red-700" />
            )}
            Tenant isolation release
          </div>
          <h2 className="mt-2 font-heading text-xl font-semibold text-slate-950">
            {ready ? 'Ready' : 'Blocked'}
          </h2>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-700">{nextAction}</p>
        </div>
        <div className="grid min-w-[300px] grid-cols-3 gap-2 rounded-lg border border-white/70 bg-white/85 p-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Flag</p>
            <p className={`mt-1 text-sm font-semibold ${readiness?.environment_flag_enabled ? 'text-emerald-700' : 'text-amber-700'}`}>
              {readiness?.environment_flag_enabled ? 'Enabled' : 'Disabled'}
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Checks</p>
            <p className="mt-1 text-sm font-semibold text-slate-950">{passed}/{checks.length || 0} passed</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Checked</p>
            <p className="mt-1 text-sm font-semibold text-slate-950">{formatDateTime(readiness?.checked_at)}</p>
          </div>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {blockerCategories.length > 0 ? blockerCategories.map((category) => (
          <Badge key={category} className="border border-red-200 bg-white text-red-800">
            {category.replace(/_/g, ' ')}
          </Badge>
        )) : (
          <Badge className="border border-emerald-200 bg-white text-emerald-800">No isolation blockers</Badge>
        )}
      </div>
    </section>
  );
}

function LaunchDecisionBoard({ bundles }) {
  const workspaces = bundles || [];
  const ready = workspaces.filter((bundle) => bundle?.readiness?.can_activate).length;
  const blocked = workspaces.length - ready;
  const firstBlocked = workspaces.find((bundle) => !bundle?.readiness?.can_activate);
  const firstAction = firstBlocked?.onboarding?.action_queue?.[0] || null;
  const headline = workspaces.length === 0
    ? 'No tenant workspace has been drafted yet'
    : blocked > 0
      ? `${blocked} tenant${blocked === 1 ? '' : 's'} not launch-ready`
      : 'All tenant workspaces are launch-ready';

  return (
    <section className={`rounded-lg border p-5 ${
      blocked > 0 || workspaces.length === 0
        ? 'border-amber-200 bg-amber-50/70'
        : 'border-emerald-200 bg-emerald-50/70'
    }`} data-testid="owner-launch-decision-board">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            {blocked > 0 || workspaces.length === 0 ? (
              <ShieldAlert className="h-4 w-4 text-amber-700" />
            ) : (
              <ShieldCheck className="h-4 w-4 text-emerald-700" />
            )}
            Founding member launch decision
          </div>
          <h2 className="mt-2 font-heading text-xl font-semibold text-slate-950">
            {headline}
          </h2>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-700">
            This is the owner-level truth before you invite or activate a company. A tenant is not launch-ready until identity, branding, support contacts, role rates, payroll, invoice defaults, private links, and tenant access gates are all clean.
          </p>
        </div>
        <div className="grid min-w-[280px] grid-cols-3 gap-2 rounded-lg border border-white/70 bg-white/80 p-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Total</p>
            <p className="mt-1 text-2xl font-semibold text-slate-950">{metric(workspaces.length)}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Ready</p>
            <p className="mt-1 text-2xl font-semibold text-emerald-700">{metric(ready)}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Blocked</p>
            <p className="mt-1 text-2xl font-semibold text-amber-700">{metric(blocked)}</p>
          </div>
        </div>
      </div>
      {firstAction ? (
        <div className="mt-4 rounded-md border border-amber-200 bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Next owner action</p>
          <p className="mt-1 text-sm font-semibold text-slate-950">
            {firstBlocked?.workspace?.display_name || 'Tenant'}: {firstAction.label}
          </p>
          <p className="mt-1 text-sm leading-6 text-slate-600">{firstAction.detail}</p>
          {firstAction.tenant_route ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-3 h-8 rounded-md bg-white text-xs"
              onClick={() => openTenantRoute(firstAction.tenant_route)}
            >
              {firstAction.action_label || 'Open tenant area'}
              <ArrowRight className="ml-2 h-3.5 w-3.5" />
            </Button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function ReadinessChecklist({ readiness }) {
  const checks = readiness?.checks || [];
  const blockedCapabilities = readiness?.blocked_capabilities || [];
  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-[#DDE6EA] bg-gradient-to-r from-[#F8FAFA] to-white p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-sm font-semibold text-slate-900">Owner launch decision</p>
            <p className="mt-1 text-sm text-slate-600">
              {readiness?.next_action || 'Complete the remaining tenant launch checks.'}
            </p>
          </div>
          <span className={`text-sm font-semibold ${readinessTone(readiness)}`}>
            {readiness?.completed || 0}/{readiness?.total || 0} checks
          </span>
        </div>
        {blockedCapabilities.length ? (
          <div className="mt-3 flex flex-wrap gap-2">
            {blockedCapabilities.map((capability) => (
              <Badge key={capability} variant="outline" className="border-amber-200 bg-amber-50 text-amber-800">
                {CAPABILITY_LABELS[capability] || formatStatus(capability)}
              </Badge>
            ))}
          </div>
        ) : (
          <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800">
            <ShieldCheck className="h-3.5 w-3.5" />
            No blocked launch capabilities
          </div>
        )}
      </div>
      <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
        {checks.map((check) => (
          <div key={check.id} className="rounded-md border border-[#E4E8EB] bg-[#F8FAFA] p-3">
            <div className="flex items-start gap-2">
              {check.done ? (
                <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
              ) : (
                <Clock className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
              )}
              <div>
                <p className="text-sm font-medium text-slate-900">{check.label}</p>
                <p className="mt-1 text-xs leading-5 text-slate-600">{check.detail}</p>
                {!check.done && check.missing_fields?.length ? (
                  <p className="mt-2 text-xs font-medium text-amber-700">
                    Missing: {check.missing_fields.map((field) => field.label || field.key).join(', ')}
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EntitlementSummary({ entitlements, counts = {} }) {
  const limits = entitlements?.limits || {};
  const features = entitlements?.features || {};
  const limitOverride = entitlements?.limit_override || {};
  const limitSourceLabel = limitOverride.applied
    ? 'Custom limits active'
    : limitOverride.ignored
      ? 'Plan limits restored'
      : 'Plan limits';
  const featured = [
    ['Worker limit', limits.workers_limit],
    ['Admin seats', `${metric(counts.admin_users)} / ${metric(limits.admin_users_limit)}`],
    ['Clients', limits.client_organisations_limit],
    ['Applicant invites / month', limits.applicant_invites_monthly_limit],
    ['Storage GB', limits.storage_gb_limit],
  ];
  const enabledFeatures = Object.entries(features)
    .filter(([, enabled]) => enabled)
    .slice(0, 6)
    .map(([key]) => formatStatus(key));

  return (
    <div className="grid gap-3 lg:grid-cols-2">
      <div className="rounded-md border border-[#E4E8EB] bg-[#F8FAFA] p-3">
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Plan limits</p>
          <Badge variant="outline" className="border-slate-200 bg-white text-slate-600">{limitSourceLabel}</Badge>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
          {featured.map(([label, value]) => (
            <div key={label}>
              <p className="text-slate-500">{label}</p>
              <p className="font-semibold text-slate-900">{typeof value === 'string' ? value : metric(value)}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-md border border-[#E4E8EB] bg-[#F8FAFA] p-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Enabled features</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {enabledFeatures.length ? enabledFeatures.map((feature) => (
            <Badge key={feature} variant="outline" className="border-slate-200 bg-white text-slate-700">
              {feature}
            </Badge>
          )) : <p className="text-sm text-slate-500">No feature data yet.</p>}
        </div>
      </div>
    </div>
  );
}

function OwnerActionQueue({ actions }) {
  const items = actions || [];
  return (
    <div className="rounded-md border border-[#E4E8EB] bg-[#F8FAFA] p-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">CareOps next actions</p>
      <div className="mt-3 grid gap-2 lg:grid-cols-2">
        {items.map((action, index) => (
          <div key={action.id || action.label} className="rounded-md border border-[#E4E8EB] bg-white p-3">
            <div className="flex items-start gap-3">
              <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                action.priority === 'high' ? 'bg-amber-100 text-amber-800' : 'bg-primary/10 text-primary'
              }`}>
                {index + 1}
              </span>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-semibold text-slate-900">{action.label}</p>
                  <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-600">
                    {action.owner_area}
                  </Badge>
                </div>
                <p className="mt-1 text-xs leading-5 text-slate-600">{action.detail}</p>
                {action.blocked_by?.filter(Boolean).length ? (
                  <p className="mt-2 text-xs font-medium text-amber-700">
                    Waiting on: {action.blocked_by.filter(Boolean).join(', ')}
                  </p>
                ) : null}
                {action.tenant_route ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-3 h-8 rounded-md bg-white text-xs"
                    onClick={() => openTenantRoute(action.tenant_route)}
                  >
                    {action.action_label || 'Open tenant area'}
                    <ArrowRight className="ml-2 h-3.5 w-3.5" />
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CapabilityGateBoard({ gates }) {
  const items = gates || [];
  return (
    <div className="rounded-md border border-[#E4E8EB] bg-white p-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Capability gates</p>
      <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
        {items.map((gate) => (
          <div key={gate.id} className={`rounded-md border p-3 ${
            gate.open ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'
          }`}>
            <div className="flex items-start gap-2">
              {gate.open ? (
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" />
              ) : (
                <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
              )}
              <div>
                <p className="text-sm font-semibold text-slate-900">{gate.label}</p>
                <p className={`mt-1 text-xs leading-5 ${gate.open ? 'text-emerald-800' : 'text-amber-800'}`}>
                  {gate.open
                    ? 'Open for this tenant based on current records.'
                    : `Blocked by: ${(gate.blocked_by || []).filter(Boolean).join(', ') || 'launch readiness'}`}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function OnboardingTracker({ onboarding }) {
  const phases = onboarding?.phases || [];
  const events = onboarding?.events || [];
  const risks = onboarding?.risks || [];
  const actions = onboarding?.action_queue || [];
  const gates = onboarding?.capability_gates || [];
  const progress = onboarding?.progress || {};
  const status = onboarding?.lifecycle_status || {};
  const toneClass = status.tone === 'ready'
    ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
    : status.tone === 'attention'
      ? 'border-amber-200 bg-amber-50 text-amber-800'
      : 'border-sky-200 bg-sky-50 text-sky-800';

  return (
    <div className="rounded-lg border border-[#E4E8EB] bg-white p-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <p className="text-sm font-semibold text-slate-900">Tenant onboarding tracker</p>
          <p className="mt-1 text-sm text-slate-600">
            Live tenant journey based on workspace records, launch checks, invites and tenant activity.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className={toneClass}>
            {status.label || 'Onboarding'}
          </Badge>
          <span className="text-sm font-semibold text-slate-700">
            {progress.completed || 0}/{progress.total || 0} phases
          </span>
        </div>
      </div>

      <div className="mt-4 h-2 rounded-full bg-slate-100">
        <div className="h-2 rounded-full bg-primary" style={{ width: `${progress.percent || 0}%` }} />
      </div>

      <div className="mt-4 grid gap-4 xl:grid-cols-[1.15fr_0.85fr]">
        <OwnerActionQueue actions={actions} />
        <CapabilityGateBoard gates={gates} />
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        {phases.map((phase) => (
          <div key={phase.id} className={`rounded-md border p-3 ${phase.done ? 'border-emerald-200 bg-emerald-50' : 'border-[#E4E8EB] bg-[#F8FAFA]'}`}>
            <div className="flex items-start gap-2">
              {phase.done ? (
                <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
              ) : (
                <Clock className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
              )}
              <div>
                <p className="text-sm font-semibold text-slate-900">{phase.label}</p>
                <p className="mt-1 text-xs leading-5 text-slate-600">{phase.detail}</p>
                <p className="mt-2 text-[11px] uppercase tracking-wide text-slate-500">
                  {phase.done ? `Completed: ${formatDateTime(phase.completed_at)}` : 'Waiting'}
                </p>
                {!phase.done && phase.blocked_by?.length ? (
                  <p className="mt-1 text-xs font-medium text-amber-700">
                    Blocked by: {phase.blocked_by.join(', ')}
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 grid gap-4 xl:grid-cols-[0.9fr_1.1fr]">
        <div className="rounded-md border border-[#E4E8EB] bg-[#F8FAFA] p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Launch risks</p>
          <div className="mt-3 space-y-2">
            {risks.length ? risks.map((risk) => (
              <div key={risk.id} className="rounded-md border border-amber-200 bg-white p-2">
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm font-semibold text-slate-900">{risk.label}</p>
                  <Badge variant="outline" className={risk.severity === 'high' ? 'border-red-200 bg-red-50 text-red-700' : 'border-amber-200 bg-amber-50 text-amber-800'}>
                    {risk.severity}
                  </Badge>
                </div>
                <p className="mt-1 text-xs leading-5 text-slate-600">{risk.detail}</p>
              </div>
            )) : (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2 text-sm font-medium text-emerald-800">
                No launch risks currently reported by the backend.
              </div>
            )}
          </div>
        </div>

        <div className="rounded-md border border-[#E4E8EB] bg-[#F8FAFA] p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Event history</p>
          <div className="mt-3 space-y-3">
            {events.length ? events.map((event) => (
              <div key={event.id} className="border-l-2 border-primary/30 pl-3">
                <p className="text-sm font-semibold text-slate-900">{event.label}</p>
                <p className="mt-0.5 text-xs leading-5 text-slate-600">{event.detail}</p>
                <p className="mt-1 text-[11px] uppercase tracking-wide text-slate-500">
                  {formatDateTime(event.occurred_at)}{event.actor ? ` | ${event.actor}` : ''}
                </p>
              </div>
            )) : (
              <p className="text-sm text-slate-500">No onboarding events recorded yet.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function LaunchProofPanel({ proof }) {
  const checks = proof?.checks || [];
  const outstandingChecks = checks.filter((check) => !check.done);
  const completedChecks = checks.filter((check) => check.done);
  const proven = Boolean(proof?.is_launch_proven);
  const tone = proven
    ? 'border-emerald-200 bg-emerald-50/60'
    : 'border-amber-200 bg-amber-50/60';

  return (
    <div className={`rounded-lg border p-4 ${tone}`} data-testid="tenant-launch-proof">
      <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            {proven ? (
              <ShieldCheck className="h-4 w-4 text-emerald-700" />
            ) : (
              <ShieldAlert className="h-4 w-4 text-amber-700" />
            )}
            First tenant launch proof
          </div>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-700">
            {proof?.summary || 'Prove setup, tenant access, private applicant links, worker onboarding and generated documents before widening beta access.'}
          </p>
        </div>
        <div className="rounded-md border border-white/70 bg-white/80 px-4 py-3 text-right">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Proof complete</p>
          <p className="mt-1 text-2xl font-semibold text-slate-950">
            {proof?.completed || 0}/{proof?.total || 0}
          </p>
        </div>
      </div>

      {!proven ? (
        <div className="mt-4 rounded-md border border-amber-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Next launch-proof action</p>
              <p className="mt-1 text-sm font-semibold text-slate-950">{proof?.next_area || 'CareOps Admin'}</p>
              <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-700">{proof?.next_action}</p>
            </div>
            {proof?.next_tenant_route ? (
              <Button
                type="button"
                variant="outline"
                className="w-full justify-center rounded-md bg-white lg:w-auto"
                onClick={() => openTenantRoute(proof.next_tenant_route)}
              >
                Open tenant route
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <Badge className="w-fit border-amber-200 bg-amber-50 text-amber-800">CareOps action</Badge>
            )}
          </div>
        </div>
      ) : null}

      {outstandingChecks.length ? (
        <div className="mt-4">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Proof actions still needed</p>
            <Badge className="border-amber-200 bg-amber-50 text-amber-800">
              {outstandingChecks.length} open
            </Badge>
          </div>
          <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {outstandingChecks.map((check) => (
              <div key={check.id} className="rounded-md border border-amber-200 bg-white p-3 shadow-sm">
                <div className="flex items-start gap-2">
                  <Clock className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-900">{check.label}</p>
                    <p className="mt-1 text-xs leading-5 text-slate-600">{check.detail}</p>
                    <p className="mt-2 text-xs font-medium leading-5 text-amber-700">{check.next_action}</p>
                    <div className="mt-3">
                      {check.tenant_route ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8 rounded-md bg-white text-xs"
                          onClick={() => openTenantRoute(check.tenant_route)}
                        >
                          {check.action_label || 'Open tenant area'}
                          <ArrowRight className="ml-2 h-3.5 w-3.5" />
                        </Button>
                      ) : (
                        <Badge className="border-amber-200 bg-amber-50 text-amber-800">
                          {check.action_label || 'Handle in CareOps Admin'}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="mt-4 rounded-md border border-emerald-200 bg-white p-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-emerald-800">
            <CheckCircle className="h-4 w-4" />
            Launch proof is complete
          </div>
          <p className="mt-1 text-sm text-slate-600">Keep monitoring this tenant during beta, but the first proof path is no longer blocked.</p>
        </div>
      )}

      <div className="mt-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Completed proof evidence</p>
        <div className="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-3">
          {completedChecks.map((check) => (
            <div key={check.id} className="rounded-md border border-emerald-200 bg-white/90 p-3">
              <div className="flex items-start gap-2">
                <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                <div>
                  <p className="text-sm font-semibold text-slate-900">{check.label}</p>
                  <p className="mt-1 text-xs leading-5 text-slate-600">{check.detail}</p>
                </div>
              </div>
            </div>
          ))}
          {!completedChecks.length ? (
            <div className="rounded-md border border-dashed border-[#DDE6EA] bg-white/70 p-3 text-sm text-slate-500">
              No launch proof has been completed yet.
            </div>
          ) : null}
        </div>
      </div>

      <div className="mt-4 grid gap-2 sm:grid-cols-5">
        {Object.entries(proof?.proof_counts || {}).map(([key, value]) => (
          <div key={key} className={`rounded-md border bg-white/80 p-3 ${
            Number(value || 0) > 0 ? 'border-emerald-200' : 'border-[#E4E8EB]'
          }`}>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{formatStatus(key)}</p>
            <p className="mt-1 text-lg font-semibold text-slate-950">{metric(value)}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function CreateWorkspacePanel({
  workspaceForm,
  setWorkspaceForm,
  createWorkspace,
  plans,
  saving,
  loading,
  onClose,
}) {
  return (
    <section className="rounded-lg border border-[#DDE6EA] bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-center gap-2">
          <Plus className="h-5 w-5 text-primary" />
          <div>
            <h2 className="font-heading text-lg font-semibold text-slate-950">Create private-beta workspace</h2>
            <p className="mt-1 text-sm text-slate-600">Draft the company first, then complete its launch gates before access is opened.</p>
          </div>
        </div>
        {onClose ? (
          <Button type="button" variant="ghost" size="icon" className="rounded-md" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        ) : null}
      </div>
      <form onSubmit={createWorkspace} className="mt-4 grid gap-4 lg:grid-cols-6">
        <div className="space-y-2 lg:col-span-2">
          <Label htmlFor="display_name">Workplace name *</Label>
          <Input id="display_name" value={workspaceForm.display_name} onChange={(e) => setWorkspaceForm((f) => ({ ...f, display_name: e.target.value }))} required />
        </div>
        <div className="space-y-2 lg:col-span-2">
          <Label htmlFor="legal_name">Legal name</Label>
          <Input id="legal_name" value={workspaceForm.legal_name} onChange={(e) => setWorkspaceForm((f) => ({ ...f, legal_name: e.target.value }))} />
        </div>
        <div className="space-y-2 lg:col-span-2">
          <Label htmlFor="plan_id">Subscription</Label>
          <select
            id="plan_id"
            value={workspaceForm.plan_id}
            onChange={(e) => setWorkspaceForm((f) => ({ ...f, plan_id: e.target.value }))}
            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
          >
            {plans.map((plan) => (
              <option key={plan.id} value={plan.id}>{plan.name}</option>
            ))}
          </select>
        </div>
        <div className="space-y-2 lg:col-span-2">
          <Label htmlFor="contact_email">Tenant owner email</Label>
          <Input id="contact_email" type="email" value={workspaceForm.contact_email} onChange={(e) => setWorkspaceForm((f) => ({ ...f, contact_email: e.target.value }))} />
        </div>
        <div className="space-y-2 lg:col-span-2">
          <Label htmlFor="contact_phone">Contact phone</Label>
          <Input id="contact_phone" value={workspaceForm.contact_phone} onChange={(e) => setWorkspaceForm((f) => ({ ...f, contact_phone: e.target.value }))} />
        </div>
        <div className="space-y-2 lg:col-span-2">
          <Label htmlFor="notes">Owner notes</Label>
          <Input id="notes" value={workspaceForm.notes} onChange={(e) => setWorkspaceForm((f) => ({ ...f, notes: e.target.value }))} />
        </div>
        <div className="lg:col-span-6">
          <Button type="submit" disabled={saving || loading} className="rounded-md bg-primary text-white">
            {saving ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
            Create draft
          </Button>
        </div>
      </form>
    </section>
  );
}

function TenantDirectoryCard({ bundle, plans, onOpen }) {
  const workspace = bundle.workspace || {};
  const subscription = bundle.subscription || {};
  const counts = bundle.operational_counts || {};
  const readiness = bundle.readiness || {};
  const onboarding = bundle.onboarding || {};
  const launchProof = bundle.launch_proof || {};
  const action = onboarding.action_queue?.[0] || null;
  const blocked = !readiness.can_activate || !launchProof.is_launch_proven;

  return (
    <article className="rounded-lg border border-[#DDE6EA] bg-white p-5 shadow-sm transition hover:border-primary/40 hover:shadow-md">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            <h3 className="font-heading text-xl font-semibold text-slate-950">{workspace.display_name}</h3>
            <Badge className={`border ${statusTone(workspace.status)}`}>{formatStatus(workspace.status)}</Badge>
            <Badge variant="outline" className={blocked ? 'border-amber-200 bg-amber-50 text-amber-800' : 'border-emerald-200 bg-emerald-50 text-emerald-800'}>
              {blocked ? 'needs launch proof' : 'ready for beta'}
            </Badge>
          </div>
          <p className="mt-2 text-sm text-slate-600">
            {workspace.legal_name || 'No legal name'} | {workspace.contact_email || 'No tenant owner email'} | {planLabel(plans, subscription.plan_id)}
          </p>
        </div>
        <Button type="button" className="rounded-md" onClick={onOpen}>
          Open tenant
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <SummaryTile icon={Users} label="Applicants" value={counts.applicants} />
        <SummaryTile icon={Users} label="Workers" value={counts.workers} />
        <SummaryTile icon={ShieldCheck} label="Active" value={counts.active_workers} />
        <SummaryTile icon={ClipboardCheck} label="Docs review" value={counts.documents_awaiting_review} tone={counts.documents_awaiting_review ? 'text-amber-700' : 'text-slate-900'} />
        <SummaryTile icon={Users} label="Admin seats" value={counts.admin_users} />
      </div>

      <div className="mt-4 grid gap-3 lg:grid-cols-[1fr_auto] lg:items-center">
        <div className="rounded-md border border-[#E4E8EB] bg-[#F8FAFA] p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Next owner action</p>
          <p className="mt-1 text-sm font-semibold text-slate-900">{action?.label || 'Monitor controlled launch'}</p>
          <p className="mt-1 text-xs leading-5 text-slate-600">
            {action?.detail || 'This tenant has no immediate owner blocker. Keep watching tenant activity, document review and worker readiness.'}
          </p>
        </div>
        <div className="rounded-md border border-[#E4E8EB] bg-white p-3 text-right">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Launch gates</p>
          <p className={`mt-1 text-xl font-semibold ${readiness.can_activate ? 'text-emerald-700' : 'text-amber-700'}`}>
            {readiness.completed || 0}/{readiness.total || 0}
          </p>
        </div>
      </div>
    </article>
  );
}

function BetaTermsPanel({ bundle, betaTermsForm, setBetaTermsForms, recordBetaTerms }) {
  const workspace = bundle.workspace || {};
  const readiness = bundle.readiness || {};
  const betaTermsReady = Boolean((readiness.checks || []).find((check) => check.id === 'beta_terms')?.done);

  return (
    <div className={`rounded-lg border p-4 ${betaTermsReady ? 'border-emerald-200 bg-emerald-50/40' : 'border-amber-200 bg-amber-50/40'}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-slate-900">Private-beta terms</p>
          <p className="mt-1 text-sm leading-6 text-slate-600">
            Record agreement, privacy acceptance, data processing terms, retention rules and backup handling before live beta.
          </p>
        </div>
        <Badge className={`shrink-0 ${betaTermsReady ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
          {betaTermsReady ? 'recorded' : 'required'}
        </Badge>
      </div>
      <div className="mt-3 rounded-md border border-[#E4E8EB] bg-white p-3 text-xs leading-5 text-slate-700">
        <p className="font-semibold text-slate-900">Agreement summary</p>
        <p className="mt-1">
          Invite-only beta access, tenant-controlled data accuracy, CareOps platform processing, tenant isolation, audit logging, document history, retention/deletion limits, and encrypted backup handling are recorded as the launch-control truth.
        </p>
      </div>
      {betaTermsReady ? (
        <div className="mt-4 rounded-md border border-emerald-200 bg-white p-3 text-sm text-emerald-800">
          <p className="font-semibold">Private-beta terms are recorded for this tenant. This launch gate is clear.</p>
          {bundle.beta_agreement?.record?.accepted_at ? (
            <p className="mt-1 text-xs leading-5 text-emerald-700">
              Accepted by {bundle.beta_agreement.record.accepted_by_name || 'tenant contact'} ({bundle.beta_agreement.record.accepted_by_email || 'email not recorded'}) on {formatDateTime(bundle.beta_agreement.record.accepted_at)}.
            </p>
          ) : null}
          {bundle.beta_agreement?.record?.terms_version ? (
            <p className="mt-1 text-xs leading-5 text-emerald-700">
              Terms: {bundle.beta_agreement.record.terms_version} | Retention: {bundle.beta_agreement.record.retention_policy_version}
            </p>
          ) : null}
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          <div className="grid gap-3 lg:grid-cols-2">
            <Input
              placeholder="Accepted by"
              value={betaTermsForm.accepted_by_name}
              onChange={(e) => setBetaTermsForms((prev) => ({
                ...prev,
                [workspace.id]: { ...betaTermsForm, accepted_by_name: e.target.value },
              }))}
            />
            <Input
              placeholder="Acceptance email"
              type="email"
              value={betaTermsForm.accepted_by_email}
              onChange={(e) => setBetaTermsForms((prev) => ({
                ...prev,
                [workspace.id]: { ...betaTermsForm, accepted_by_email: e.target.value },
              }))}
            />
          </div>
          <div className="grid gap-2 rounded-md border border-[#E4E8EB] bg-white p-3">
            {BETA_ACCEPTANCE_CHECKS.map((check) => (
              <label key={check.key} className="flex items-start gap-3 rounded-md px-2 py-1.5 text-sm leading-5 text-slate-700">
                <Checkbox
                  className="mt-0.5"
                  checked={Boolean(betaTermsForm[check.key])}
                  onCheckedChange={(checked) => setBetaTermsForms((prev) => ({
                    ...prev,
                    [workspace.id]: { ...betaTermsForm, [check.key]: checked === true },
                  }))}
                />
                <span>{check.label}</span>
              </label>
            ))}
          </div>
          <div className="grid gap-3 lg:grid-cols-[1fr_auto] lg:items-start">
            <Textarea
              placeholder="Notes, e.g. accepted by email or call with tenant owner"
              value={betaTermsForm.notes}
              onChange={(e) => setBetaTermsForms((prev) => ({
                ...prev,
                [workspace.id]: { ...betaTermsForm, notes: e.target.value },
              }))}
              className="min-h-20"
            />
            <Button type="button" className="rounded-md" onClick={() => recordBetaTerms(workspace.id, workspace)}>
              <ShieldCheck className="mr-2 h-4 w-4" />
              Record agreement
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function accessTone(status, active) {
  if (active || status === 'accepted') return 'border-emerald-200 bg-emerald-50 text-emerald-800';
  if (status === 'failed') return 'border-red-200 bg-red-50 text-red-800';
  if (status === 'sent' || status === 'ready' || status === 'draft' || status === 'expired') return 'border-amber-200 bg-amber-50 text-amber-800';
  return 'border-slate-200 bg-slate-50 text-slate-700';
}

export function TenantInvitePanel({ bundle, inviteForm, setInviteForms, createInviteDraft, sendInvitation, resendInvitation, revokeInvitation }) {
  const workspace = bundle.workspace || {};
  const ownerAccess = bundle.readiness?.owner_access || {};
  const ownerInvite = ownerAccess.invitation || (bundle.invitations || []).find((invite) => invite.workspace_role === 'workspace_owner') || null;
  const counts = bundle.operational_counts || {};
  const adminLimit = Number(bundle.entitlements?.limits?.admin_users_limit || 0);
  const status = ownerAccess.status || ownerInvite?.status || 'none';
  const label = ownerAccess.label || 'No owner invitation';
  const nextAction = ownerAccess.active
    ? 'Open tenant access'
    : !ownerInvite
      ? 'Prepare owner invitation'
      : status === 'draft'
        ? 'Send owner invitation'
        : status === 'failed'
          ? 'Resend invitation'
          : status === 'expired'
            ? 'Resend invitation'
            : status === 'sent'
              ? 'Revoke invitation'
              : 'Complete setup before sending';
  const runNextAction = () => {
    if (ownerAccess.active) return openTenantRoute('/portal/admin-users');
    if (!ownerInvite) return createInviteDraft(workspace.id);
    if (status === 'draft') return sendInvitation(workspace.id, ownerInvite.id);
    if (status === 'failed' || status === 'expired') return resendInvitation(workspace.id, ownerInvite.id);
    if (status === 'sent') return revokeInvitation(workspace.id, ownerInvite.id);
    return null;
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-[#DDE6EA] bg-white p-5 shadow-sm" data-testid="tenant-access-status-card">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <Crown className="h-5 w-5 text-primary" />
              <p className="text-base font-semibold text-slate-950">Tenant access</p>
              <Badge className={`border ${accessTone(status, ownerAccess.active)}`}>{label}</Badge>
            </div>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              Owner access is complete only after the invited owner accepts and has an active workspace-owner membership.
            </p>
          </div>
          <Button type="button" className="rounded-md" onClick={runNextAction} disabled={nextAction === 'Complete setup before sending'}>
            {status === 'sent' ? <X className="mr-2 h-4 w-4" /> : <Send className="mr-2 h-4 w-4" />}
            {nextAction}
          </Button>
        </div>
        <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-md border border-[#E4E8EB] bg-[#F8FAFA] p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Current owner</p>
            <p className="mt-1 text-sm font-semibold text-slate-950">{ownerAccess.active ? 'Active workspace owner' : 'Not active yet'}</p>
          </div>
          <div className="rounded-md border border-[#E4E8EB] bg-[#F8FAFA] p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Invited person</p>
            <p className="mt-1 text-sm font-semibold text-slate-950">{ownerInvite?.contact_name || inviteForm.contact_name || 'Not prepared'}</p>
            <p className="text-xs text-slate-500">{ownerInvite?.contact_email || inviteForm.contact_email || 'No email recorded'}</p>
          </div>
          <div className="rounded-md border border-[#E4E8EB] bg-[#F8FAFA] p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Role and dates</p>
            <p className="mt-1 text-sm font-semibold text-slate-950">{ownerInvite?.role_label || 'Workspace Owner'}</p>
            <p className="text-xs text-slate-500">Sent {formatDateTime(ownerInvite?.sent_at)} | Expires {formatDateTime(ownerInvite?.expires_at)}</p>
          </div>
          <div className="rounded-md border border-[#E4E8EB] bg-[#F8FAFA] p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Admin seats</p>
            <p className="mt-1 text-sm font-semibold text-slate-950">{metric(counts.admin_users)}{adminLimit ? ` / ${metric(adminLimit)}` : ''}</p>
            <p className="text-xs text-slate-500">Pending invitations reserve seats before acceptance.</p>
          </div>
        </div>
      </div>

      {!ownerInvite && !ownerAccess.active ? (
        <div className="rounded-lg border border-[#E4E8EB] bg-white p-4">
          <p className="text-sm font-semibold text-slate-900">Prepare owner invitation</p>
          <div className="mt-3 grid gap-3 lg:grid-cols-[1fr_1fr_1fr_auto]">
            <Input placeholder="Owner name" value={inviteForm.contact_name} onChange={(e) => setInviteForms((prev) => ({ ...prev, [workspace.id]: { ...inviteForm, contact_name: e.target.value, role: 'workspace_owner' } }))} />
            <Input placeholder="Owner email" type="email" value={inviteForm.contact_email} onChange={(e) => setInviteForms((prev) => ({ ...prev, [workspace.id]: { ...inviteForm, contact_email: e.target.value, role: 'workspace_owner' } }))} />
            <Textarea placeholder="Notes" value={inviteForm.notes} onChange={(e) => setInviteForms((prev) => ({ ...prev, [workspace.id]: { ...inviteForm, notes: e.target.value, role: 'workspace_owner' } }))} className="min-h-10" />
            <Button type="button" className="rounded-md" onClick={() => createInviteDraft(workspace.id)}>
              <Plus className="mr-2 h-4 w-4" />
              Prepare owner invitation
            </Button>
          </div>
        </div>
      ) : null}

      {(bundle.invitations || []).length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-[#E4E8EB] bg-white p-4">
          <table className="w-full text-left text-sm">
            <thead className="text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="py-2 pr-3">Name</th>
                <th className="py-2 pr-3">Email</th>
                <th className="py-2 pr-3">Role</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Expiry</th>
                <th className="py-2 pr-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {(bundle.invitations || []).map((invite) => (
                <tr key={invite.id} className="border-t border-[#E4E8EB]">
                  <td className="py-2 pr-3 text-slate-900">{invite.contact_name}</td>
                  <td className="py-2 pr-3 text-slate-600">{invite.contact_email}</td>
                  <td className="py-2 pr-3 text-slate-600">{invite.role_label || formatStatus(invite.workspace_role || invite.role)}</td>
                  <td className="py-2 pr-3 text-slate-600">{formatStatus(invite.status)}</td>
                  <td className="py-2 pr-3 text-slate-600">{formatDateTime(invite.expires_at)}</td>
                  <td className="py-2 pr-3">
                    {invite.status === 'draft' ? (
                      <Button type="button" size="sm" variant="outline" onClick={() => sendInvitation(workspace.id, invite.id)}>Send</Button>
                    ) : ['sent', 'failed', 'expired'].includes(invite.status) ? (
                      <div className="flex gap-2">
                        <Button type="button" size="sm" variant="outline" onClick={() => resendInvitation(workspace.id, invite.id)}>Resend</Button>
                        <Button type="button" size="sm" variant="ghost" onClick={() => revokeInvitation(workspace.id, invite.id)}>Revoke</Button>
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PlanAddonsPanel({ bundle, plans, addons, toggleAddon }) {
  const workspace = bundle.workspace || {};
  const subscription = bundle.subscription || {};
  const counts = bundle.operational_counts || {};
  const activeAddonIds = new Set((bundle.addons || []).filter((addon) => addon.status === 'active').map((addon) => addon.addon_id));

  return (
    <div className="space-y-4">
      <EntitlementSummary entitlements={bundle.entitlements} counts={counts} />
      <div className="rounded-lg border border-[#E4E8EB] bg-white p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-slate-900">Plan and add-ons</p>
            <p className="mt-1 text-sm text-slate-600">
              {planLabel(plans, subscription.plan_id)} | {subscription.subscription_status || 'subscription not set'}
            </p>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {addons.map((addon) => {
            const isActive = activeAddonIds.has(addon.id);
            return (
              <Button
                key={addon.id}
                type="button"
                variant={isActive ? 'default' : 'outline'}
                size="sm"
                className="rounded-md"
                onClick={() => toggleAddon(workspace.id, addon.id, isActive ? 'active' : 'cancelled')}
              >
                {isActive && <CheckCircle className="mr-1 h-3.5 w-3.5" />}
                {addon.name}
              </Button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const TENANT_DETAIL_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'launch', label: 'Launch proof' },
  { id: 'subscription', label: 'Subscription' },
  { id: 'access', label: 'Access' },
];

function TenantDetailPage({
  bundle,
  plans,
  addons,
  activeTab,
  setActiveTab,
  onBack,
  updateWorkspace,
  toggleAddon,
  inviteForm,
  setInviteForms,
  createInviteDraft,
  sendInvitation,
  resendInvitation,
  revokeInvitation,
  betaTermsForm,
  setBetaTermsForms,
  recordBetaTerms,
}) {
  const workspace = bundle.workspace || {};
  const subscription = bundle.subscription || {};
  const counts = bundle.operational_counts || {};
  const readiness = bundle.readiness || {};
  const onboarding = bundle.onboarding || {};
  const launchProof = bundle.launch_proof || {};

  return (
    <section className="space-y-5">
      <Button type="button" variant="ghost" className="rounded-md px-0 text-primary hover:bg-transparent" onClick={onBack}>
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to tenant list
      </Button>

      <article className="rounded-lg border border-[#DDE6EA] bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" />
              <h2 className="font-heading text-2xl font-semibold text-slate-950">{workspace.display_name}</h2>
              <Badge className={`border ${statusTone(workspace.status)}`}>{formatStatus(workspace.status)}</Badge>
              {!readiness.can_activate && (
                <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-800">
                  activation blocked
                </Badge>
              )}
            </div>
            <p className="mt-2 text-sm text-slate-600">
              {workspace.legal_name || 'No legal name'} | {workspace.contact_email || 'No tenant owner email'} | {bundle.membership_count || 0} member record(s)
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <select
              value={workspace.status || 'invite_draft'}
              onChange={(e) => updateWorkspace(workspace.id, { status: e.target.value })}
              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
            >
              {STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>{formatStatus(status)}</option>
              ))}
            </select>
            <select
              value={subscription.plan_id || 'essential'}
              onChange={(e) => updateWorkspace(workspace.id, { plan_id: e.target.value })}
              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
            >
              {plans.map((plan) => (
                <option key={plan.id} value={plan.id}>{plan.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8">
          <SummaryTile icon={Users} label="Applicants" value={counts.applicants} />
          <SummaryTile icon={Users} label="Workers" value={counts.workers} />
          <SummaryTile icon={Users} label="Admin seats" value={counts.admin_users} />
          <SummaryTile icon={ShieldCheck} label="Active" value={counts.active_workers} />
          <SummaryTile icon={ClipboardCheck} label="Docs review" value={counts.documents_awaiting_review} tone={counts.documents_awaiting_review ? 'text-amber-700' : 'text-slate-900'} />
          <SummaryTile icon={Building2} label="Staffing" value={counts.open_staffing_requests} />
          <SummaryTile icon={MailWarning} label="Email fails" value={counts.failed_email_requests} tone={counts.failed_email_requests ? 'text-red-700' : 'text-slate-900'} />
          <SummaryTile icon={PauseCircle} label="Support" value={counts.open_support_chats} tone={counts.open_support_chats ? 'text-amber-700' : 'text-slate-900'} />
        </div>
      </article>

      <div className="flex flex-wrap gap-2 rounded-lg border border-[#E4E8EB] bg-white p-2">
        {TENANT_DETAIL_TABS.map((tab) => (
          <Button
            key={tab.id}
            type="button"
            variant={activeTab === tab.id ? 'default' : 'ghost'}
            className="rounded-md"
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </Button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <div className="space-y-5">
          <ReadinessChecklist readiness={readiness} />
          <OnboardingTracker onboarding={onboarding} />
        </div>
      )}

      {activeTab === 'launch' && (
        <div className="space-y-5">
          <LaunchProofPanel proof={launchProof} />
          <BetaTermsPanel
            bundle={bundle}
            betaTermsForm={betaTermsForm}
            setBetaTermsForms={setBetaTermsForms}
            recordBetaTerms={recordBetaTerms}
          />
        </div>
      )}

      {activeTab === 'subscription' && (
        <PlanAddonsPanel
          bundle={bundle}
          plans={plans}
          addons={addons}
          toggleAddon={toggleAddon}
        />
      )}

      {activeTab === 'access' && (
        <TenantInvitePanel
          bundle={bundle}
          inviteForm={inviteForm}
          setInviteForms={setInviteForms}
          createInviteDraft={createInviteDraft}
          sendInvitation={sendInvitation}
          resendInvitation={resendInvitation}
          revokeInvitation={revokeInvitation}
        />
      )}
    </section>
  );
}

export default function CareOpsOwnerPage() {
  const { token, isPlatformOwner } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [accessError, setAccessError] = useState('');
  const [data, setData] = useState({ workspaces: [], plans: [], addons: [], platform_health: {} });
  const [workspaceForm, setWorkspaceForm] = useState(emptyWorkspaceForm);
  const [inviteForms, setInviteForms] = useState({});
  const [betaTermsForms, setBetaTermsForms] = useState({});
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState('');
  const [activeTenantTab, setActiveTenantTab] = useState('overview');
  const [showCreateWorkspace, setShowCreateWorkspace] = useState(false);

  const headers = useMemo(() => authHeaders(token), [token]);

  const loadOwnerData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/owner/workspaces`, { headers: authHeaders(token) });
      setAccessError('');
      setData(res.data);
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Could not load CareOps owner console.';
      if (error?.response?.status === 403) {
        setAccessError(detail);
      }
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadOwnerData();
  }, [loadOwnerData]);

  const createWorkspace = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(workspaceForm).map(([key, value]) => [key, typeof value === 'string' ? value.trim() : value]),
      );
      await axios.post(`${API}/owner/workspaces`, payload, { headers });
      setWorkspaceForm(emptyWorkspaceForm);
      toast.success('Workspace draft created.');
      await loadOwnerData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not create workspace draft.');
    } finally {
      setSaving(false);
    }
  };

  const updateWorkspace = async (workspaceId, patch) => {
    try {
      await axios.patch(`${API}/owner/workspaces/${workspaceId}`, patch, { headers });
      toast.success('Workspace updated.');
      await loadOwnerData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not update workspace.');
    }
  };

  const toggleAddon = async (workspaceId, addonId, currentStatus) => {
    try {
      await axios.post(
        `${API}/owner/workspaces/${workspaceId}/addons`,
        { addon_id: addonId, status: currentStatus === 'active' ? 'cancelled' : 'active', quantity: 1 },
        { headers },
      );
      toast.success('Add-on updated.');
      await loadOwnerData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not update add-on.');
    }
  };

  const createInviteDraft = async (workspaceId) => {
    const form = inviteForms[workspaceId] || emptyInviteForm;
    try {
      await axios.post(`${API}/owner/workspaces/${workspaceId}/invitation-drafts`, form, { headers });
      setInviteForms((prev) => ({ ...prev, [workspaceId]: emptyInviteForm }));
      toast.success('Invitation draft prepared.');
      await loadOwnerData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not prepare invitation draft.');
    }
  };

  const sendInvitation = async (workspaceId, invitationId) => {
    try {
      await axios.post(`${API}/owner/workspaces/${workspaceId}/invitations/${invitationId}/send`, {}, { headers });
      toast.success('Owner invitation action completed.');
      await loadOwnerData();
    } catch (error) {
      toast.error(error?.response?.data?.detail?.message || error?.response?.data?.detail || 'Could not send owner invitation.');
    }
  };

  const resendInvitation = async (workspaceId, invitationId) => {
    try {
      await axios.post(`${API}/owner/workspaces/${workspaceId}/invitations/${invitationId}/resend`, {}, { headers });
      toast.success('Owner invitation refreshed.');
      await loadOwnerData();
    } catch (error) {
      toast.error(error?.response?.data?.detail?.message || error?.response?.data?.detail || 'Could not resend owner invitation.');
    }
  };

  const revokeInvitation = async (workspaceId, invitationId) => {
    try {
      await axios.post(`${API}/owner/workspaces/${workspaceId}/invitations/${invitationId}/revoke`, {}, { headers });
      toast.success('Owner invitation revoked.');
      await loadOwnerData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not revoke owner invitation.');
    }
  };

  const recordBetaTerms = async (workspaceId, workspace = {}) => {
    const form = betaTermsForms[workspaceId] || emptyBetaTermsForm;
    const payload = {
      accepted_by_name: (form.accepted_by_name || workspace.display_name || '').trim(),
      accepted_by_email: (form.accepted_by_email || workspace.contact_email || '').trim(),
      notes: (form.notes || '').trim(),
      authority_confirmed: Boolean(form.authority_confirmed),
      beta_terms_accepted: Boolean(form.beta_terms_accepted),
      privacy_notice_accepted: Boolean(form.privacy_notice_accepted),
      data_processing_terms_accepted: Boolean(form.data_processing_terms_accepted),
      retention_backup_terms_accepted: Boolean(form.retention_backup_terms_accepted),
    };
    if (!payload.accepted_by_name || !payload.accepted_by_email) {
      toast.error('Enter who accepted the beta terms and the acceptance email.');
      return;
    }
    const missingAcceptance = BETA_ACCEPTANCE_CHECKS.filter((check) => !payload[check.key]);
    if (missingAcceptance.length > 0) {
      toast.error('Confirm every beta agreement and data-protection acceptance before recording.');
      return;
    }
    try {
      await axios.post(`${API}/owner/workspaces/${workspaceId}/beta-terms`, payload, { headers });
      setBetaTermsForms((prev) => ({ ...prev, [workspaceId]: emptyBetaTermsForm }));
      toast.success('Private-beta terms recorded.');
      await loadOwnerData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not record beta terms.');
    }
  };

  if (!isPlatformOwner()) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-5 text-amber-900">
          CareOps owner access is restricted to the platform owner.
        </div>
      </div>
    );
  }

  if (accessError) {
    return (
      <div className="min-h-[60vh] bg-[#F6F8F9] p-4 sm:p-6 lg:p-8">
        <div className="mx-auto max-w-3xl rounded-lg border border-amber-200 bg-amber-50 p-6 text-amber-950">
          <div className="flex items-start gap-3">
            <ShieldAlert className="mt-0.5 h-5 w-5 flex-shrink-0" />
            <div>
              <h1 className="text-lg font-semibold">This login cannot access the CareOps owner console</h1>
              <p className="mt-2 text-sm leading-6">
                {accessError}
              </p>
              <p className="mt-3 text-sm leading-6">
                Use a separate platform-owner login that is not assigned to any tenant workspace.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const bundles = data.workspaces || [];
  const plans = data.plans || [];
  const addons = data.addons || [];
  const platformHealth = data.platform_health || {};
  const selectedBundle = bundles.find((bundle) => bundle.workspace?.id === selectedWorkspaceId) || null;

  return (
    <div className="min-h-screen bg-[#F6F8F9] p-4 sm:p-6 lg:p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <header className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-primary">
              <Crown className="h-4 w-4" />
              CareOps platform owner
            </div>
            <h1 className="mt-2 text-2xl font-heading font-semibold text-slate-950 sm:text-3xl">
              Owner control room
            </h1>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
              Create private-beta workspaces, control subscription access, watch launch readiness and keep external company access locked until the safety gate is passed.
            </p>
          </div>
          <Button variant="outline" onClick={loadOwnerData} disabled={loading} className="rounded-md">
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </header>

        <div className={`rounded-lg border p-4 text-sm ${data.safe_to_activate_external_logins ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : 'border-amber-200 bg-amber-50 text-amber-900'}`}>
          <div className="flex gap-3">
            {data.safe_to_activate_external_logins ? (
              <ShieldCheck className="mt-0.5 h-5 w-5 flex-shrink-0" />
            ) : (
              <ShieldAlert className="mt-0.5 h-5 w-5 flex-shrink-0" />
            )}
            <div>
              <p className="font-semibold">{data.safe_to_activate_external_logins ? 'External activation flag enabled' : 'Safety hold: tenant invites stay as drafts'}</p>
              <p className="mt-1">{data.safety_note}</p>
            </div>
          </div>
        </div>

        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          <SummaryTile icon={Building2} label="Workspaces" value={platformHealth.workspace_count} />
          <SummaryTile icon={ShieldCheck} label="Live beta or active" value={platformHealth.live_workspace_count} tone="text-emerald-700" />
          <SummaryTile icon={Users} label="Open support" value={platformHealth.open_support_chats} tone={platformHealth.open_support_chats ? 'text-amber-700' : 'text-slate-900'} />
          <SummaryTile icon={MailWarning} label="Failed emails" value={platformHealth.failed_email_requests} tone={platformHealth.failed_email_requests ? 'text-red-700' : 'text-slate-900'} />
          <SummaryTile icon={AlertTriangle} label="Legacy rows" value={platformHealth.legacy_records_missing_workspace_id} tone={platformHealth.legacy_records_missing_workspace_id ? 'text-amber-700' : 'text-slate-900'} />
        </section>

        <PlatformProspectInbox token={token} />

        <TenantIsolationPanel readiness={data.tenant_isolation_readiness} />

        <LaunchDecisionBoard bundles={bundles} />

        {selectedBundle ? (
          <TenantDetailPage
            bundle={selectedBundle}
            plans={plans}
            addons={addons}
            activeTab={activeTenantTab}
            setActiveTab={setActiveTenantTab}
            onBack={() => setSelectedWorkspaceId('')}
            updateWorkspace={updateWorkspace}
            toggleAddon={toggleAddon}
            inviteForm={inviteForms[selectedBundle.workspace?.id] || emptyInviteForm}
            setInviteForms={setInviteForms}
            createInviteDraft={createInviteDraft}
            sendInvitation={sendInvitation}
            resendInvitation={resendInvitation}
            revokeInvitation={revokeInvitation}
            betaTermsForm={betaTermsForms[selectedBundle.workspace?.id] || emptyBetaTermsForm}
            setBetaTermsForms={setBetaTermsForms}
            recordBetaTerms={recordBetaTerms}
          />
        ) : (
          <>
            <section className="rounded-lg border border-[#DDE6EA] bg-white p-5 shadow-sm">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <ClipboardCheck className="h-5 w-5 text-primary" />
                    <h2 className="font-heading text-lg font-semibold text-slate-950">Tenant workspace directory</h2>
                  </div>
                  <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                    Select a company to manage its launch file, subscription, admin access, private links and beta controls without crowding the owner dashboard.
                  </p>
                </div>
                <Button type="button" className="rounded-md" onClick={() => setShowCreateWorkspace((open) => !open)}>
                  {showCreateWorkspace ? <X className="mr-2 h-4 w-4" /> : <Plus className="mr-2 h-4 w-4" />}
                  {showCreateWorkspace ? 'Close draft form' : 'Create workspace'}
                </Button>
              </div>
            </section>

            {showCreateWorkspace ? (
              <CreateWorkspacePanel
                workspaceForm={workspaceForm}
                setWorkspaceForm={setWorkspaceForm}
                createWorkspace={createWorkspace}
                plans={plans}
                saving={saving}
                loading={loading}
                onClose={() => setShowCreateWorkspace(false)}
              />
            ) : null}

            <section className="space-y-4">
              {loading ? (
                <div className="rounded-lg border border-[#E4E8EB] bg-white p-8 text-center text-slate-500">
                  <RefreshCw className="mx-auto mb-3 h-6 w-6 animate-spin" />
                  Loading owner console...
                </div>
              ) : bundles.length === 0 ? (
                <div className="rounded-lg border border-[#E4E8EB] bg-white p-8 text-center text-slate-500">
                  No workspace drafts yet.
                </div>
              ) : (
                bundles.map((bundle) => (
                  <TenantDirectoryCard
                    key={bundle.workspace?.id}
                    bundle={bundle}
                    plans={plans}
                    onOpen={() => {
                      setSelectedWorkspaceId(bundle.workspace?.id || '');
                      setActiveTenantTab('overview');
                    }}
                  />
                ))
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}
